import { computed, mergeProps, reactive, toValue, unref } from 'vue';
import { useI18n } from 'vue-i18n';

import { LoadTypeDirection } from '@nova/core';
import {
  getDirectionIcon,
  getDirectionIconColor
} from '@satellite/composables/loadTypePresenterShared';
import { EntityPresenterMode } from '@satellite/composables/presenterEntityMode';
import { reactiveOption } from '@satellite/composables/presenterReactiveOption';
import { useLoadTypeDirectionLabel } from '@satellite/composables/useLoadTypeDirectionLabel';

import type {
  PresenterDisplayConfigOptions,
  PresenterDisplayOverrideProps
} from '@satellite/composables/presenterDisplayOptions';
import type { ComputedRef, Ref } from 'vue';
import type { ComposerTranslation } from 'vue-i18n';
import type { Maybe } from '@nova/core';
import type { LoadTypeDetails } from './loadTypePresenterShared';

export type LoadTypeDisplayField = 'name' | 'duration' | 'direction' | 'directionIcon';

const LOAD_TYPE_DIRECTION_KEYS: Record<string, string> = {
  [LoadTypeDirection.Inbound]: 'inbound',
  [LoadTypeDirection.Outbound]: 'outbound',
  [LoadTypeDirection.InOut]: 'inOut'
};

/**
 * Exported so list rows (e.g. `LoadTypeSelectV2`) can label a direction without
 * instantiating a display composable per row.
 */
export function translateLoadTypeDirection(t: ComposerTranslation, direction: Maybe<string>) {
  if (!direction) {
    return null;
  }

  const key = LOAD_TYPE_DIRECTION_KEYS[direction];

  return key ? t(`core.yard.loadTypeDirections.${key}`, direction) : direction;
}

export interface UseLoadTypeDisplayOptions extends PresenterDisplayConfigOptions {
  nameProps?: PresenterDisplayOverrideProps;
  durationProps?: PresenterDisplayOverrideProps;
  directionProps?: PresenterDisplayOverrideProps;
  directionIconProps?: PresenterDisplayOverrideProps;
}

export function useLoadTypeDisplay(
  loadType: Ref<LoadTypeDetails | null> | ComputedRef<LoadTypeDetails | null>,
  options: UseLoadTypeDisplayOptions = {}
) {
  const { t } = useI18n();
  const { getDirectionLabel } = useLoadTypeDirectionLabel();

  const displaySize = reactiveOption(options.size, 'regular');
  const displayMode = reactiveOption(options.mode, EntityPresenterMode.Live);
  const isFetchingValue = computed(() => (options.isFetching ? unref(options.isFetching) : false));

  const name = computed(() => loadType.value?.name ?? null);
  const duration = computed(() => loadType.value?.duration_min ?? null);
  const direction = computed(() => loadType.value?.direction ?? null);

  const directionLabel = computed(
    () => getDirectionLabel(loadType.value) ?? translateLoadTypeDirection(t, direction.value)
  );

  const directionIcon = computed(() => getDirectionIcon(direction.value));
  const directionIconColor = computed(() => getDirectionIconColor(direction.value));

  const nameAttrs = computed(() =>
    mergeProps({ 'data-testid': 'load-type-presenter-name' }, toValue(options.nameProps) ?? {})
  );

  const durationAttrs = computed(() =>
    mergeProps(
      { 'data-testid': 'load-type-presenter-duration' },
      toValue(options.durationProps) ?? {}
    )
  );

  const directionAttrs = computed(() =>
    mergeProps(
      { 'data-testid': 'load-type-presenter-direction' },
      toValue(options.directionProps) ?? {}
    )
  );

  const directionIconAttrs = computed(() =>
    mergeProps(
      {
        size: 'x-small',
        icon: directionIcon.value,
        color: directionIconColor.value
      },
      toValue(options.directionIconProps) ?? {}
    )
  );

  return reactive({
    loadType,
    mode: displayMode,
    size: displaySize,
    isFetching: isFetchingValue,
    name,
    duration,
    direction,
    directionLabel,
    directionIcon,
    directionIconColor,
    nameAttrs,
    durationAttrs,
    directionAttrs,
    directionIconAttrs
  });
}

export type LoadTypeDisplayData = ReturnType<typeof useLoadTypeDisplay>;
